import React, { useEffect, useState } from 'react';
import { storage } from '../services/api';

interface AccountStorageData {
  account_id: string;
  account_email: string;
  account_name: string;
  venue_count: number;
  cloud_storage: {
    limit_gb: number;
    used_gb: number;
    available_gb: number;
    usage_percent: number;
    recording_count: number;
  };
}

interface AccountStorageWidgetProps {
  userId: string;
  refreshInterval?: number;
}

const AccountStorageWidget: React.FC<AccountStorageWidgetProps> = ({
  userId,
  refreshInterval = 30000,
}) => {
  const [storageData, setStorageData] = useState<AccountStorageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStorage = async () => {
    if (!userId) return;

    try {
      const response = await storage.getAccountStorage(userId);
      setStorageData(response.data);
      setError(null);
      setLoading(false);
    } catch (error: any) {
      console.error('Failed to fetch account storage:', error);
      setError(error.response?.data?.error || 'Failed to load storage data');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStorage();
    const interval = setInterval(fetchStorage, refreshInterval);
    return () => clearInterval(interval);
  }, [userId, refreshInterval]);

  const getUsageColor = (percent: number) => {
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getTextColor = (percent: number) => {
    if (percent >= 90) return 'text-red-600 dark:text-red-400';
    if (percent >= 75) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <div className="text-gray-500 dark:text-gray-400">Loading account storage...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <div className="text-red-600 dark:text-red-400">{error}</div>
      </div>
    );
  }

  if (!storageData) {
    return null;
  }

  const { cloud_storage, venue_count } = storageData;

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <svg
            className="w-5 h-5 text-blue-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"
            />
          </svg>
          Account Cloud Storage
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Shared across {venue_count} {venue_count === 1 ? 'venue' : 'venues'}
        </p>
      </div>

      <div className="space-y-4">
        {/* Storage Usage */}
        <div>
          <div className="flex justify-between items-baseline mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Cloud Storage</span>
            <span className={`text-sm font-semibold ${getTextColor(cloud_storage.usage_percent)}`}>
              {cloud_storage.usage_percent.toFixed(1)}% used
            </span>
          </div>

          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
            <div
              className={`h-full ${getUsageColor(cloud_storage.usage_percent)} transition-all duration-300`}
              style={{ width: `${Math.min(cloud_storage.usage_percent, 100)}%` }}
            />
          </div>

          <div className="flex justify-between mt-2 text-xs text-gray-600 dark:text-gray-400">
            <span>{cloud_storage.used_gb.toFixed(2)} GB used</span>
            <span>{cloud_storage.limit_gb.toFixed(2)} GB limit</span>
          </div>
        </div>

        {/* Storage Details */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Available</div>
            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {cloud_storage.available_gb.toFixed(2)} GB
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Recordings</div>
            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {cloud_storage.recording_count.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Warning Messages */}
        {cloud_storage.usage_percent >= 90 && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-red-500 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Storage Almost Full
                </p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  You're using {cloud_storage.usage_percent.toFixed(1)}% of your cloud storage quota.
                  Consider upgrading or deleting old recordings.
                </p>
              </div>
            </div>
          </div>
        )}

        {cloud_storage.usage_percent >= 75 && cloud_storage.usage_percent < 90 && (
          <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-yellow-500 dark:text-yellow-400 mr-2 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Storage Running Low
                </p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  You've used {cloud_storage.usage_percent.toFixed(1)}% of your cloud storage.
                  Consider reviewing your storage usage.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountStorageWidget;
